import React from "react";
import ActionChip from "./ActionChip";
import { CALL_ACTIONS } from "./callActions.mjs";
import { dateShort, moneyK } from "./ui";
import { formatTimeRange } from "./timelineModel.mjs";

// THE STAGE LINE: what this card needs NEXT, not what last happened to it.
//
// One primary black line per card, whose content changes with the column — the
// board brief's A–E. The generic "email 8d ago" stays where it was, small and gray
// and right-aligned, because "when did anything last happen" is a different and
// lesser question than "what is this card waiting on".
//
// Everything here comes from the server already computed (GET /board-chips, same
// module as Today and Follow-ups). This file decides wording and nothing else, which
// is why a card and a Today row about one company can't contradict each other.

const days = (ts) => {
  if (!ts) return null;
  const n = Math.floor((Date.now() - new Date(ts).getTime()) / 86400000);
  return Number.isFinite(n) ? n : null;
};

/*
 * A DAY COUNT OF ZERO IS "TODAY", on every line of every card.
 *
 * "pricing sent 0d ago" and "0d in stage" were both on the board this week, and
 * this is the same ruling twice already: the cadence line in src/actions/model.js
 * and the corner label in BoardPage.js. A card is read as prose and nobody says a
 * thing happened zero days ago, so the vocabulary is settled here for every
 * phrasing this file has rather than one at a time as they turn up.
 *
 * `ago` is for a thing that happened ("viewed today"); `span` is for a length of
 * time the card has been somewhere ("in agreement today"). The lines guarded by
 * `>= 1` — the met-since-the-call ones — never reach zero and are left alone.
 */
const ago = (n) => (n === 0 ? "today" : `${n}d ago`);
const span = (n) => (n === 0 ? "today" : `${n}d`);

/** "Tue Aug 12 · 2:00 PM" — the booked meeting, in the reader's own zone. */
function meetingLabel(at, meta) {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return null;
  const day = d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  const time = formatTimeRange(at, (meta && meta.end_at) || null, {});
  return time ? `${day} · ${time}` : day;
}

/**
 * A: NEW — where the lead sits in the outreach cadence, in one line. The cadence
 * module already phrases this ("reached out 5d ago · no reply"); repeating the
 * wording here would be a second vocabulary to keep in step.
 */
function NewLine({ chip }) {
  if (!chip) return null;
  // A CALL BEHIND THE CARD BEATS THE CADENCE LINE. A lead you have spoken to is not
  // an unworked lead, and "nudged 2d ago" is an actively misleading thing to read
  // four days after the call — it invites another cold-lead nudge and, at the end
  // of the ladder, a close-out. The wording is CallLine's on purpose: the ask is
  // the same one, and a card should not phrase it two ways depending on which
  // column it happens to be sitting in.
  const since = days(chip.last_meeting_at);
  if (since !== null && since >= 1) {
    return <span className="crm-bcard__stage">met {since}d ago — update?</span>;
  }
  // And a call AHEAD of it, for the same reason: a New card with a booked call is
  // one the meeting_booked rule missed, and the cadence line under it ("reached
  // out today · no reply") reads as a lead we are still chasing.
  if (chip.next_meeting_at && new Date(chip.next_meeting_at) >= new Date() && !chip.next_meeting_declined) {
    return <span className="crm-bcard__stage">{meetingLabel(chip.next_meeting_at, chip)}</span>;
  }
  if (!chip.cadence_line) return null;
  return <span className="crm-bcard__stage">{chip.cadence_line}</span>;
}

/**
 * B: CALL SCHEDULED — the booked meeting. If it already happened and the card never
 * moved, the stage is a claim about the future the past has falsified, so the line
 * says so instead of naming a date that has passed.
 */
function CallLine({ chip }) {
  if (!chip) return null;
  const upcoming = chip.next_meeting_at && new Date(chip.next_meeting_at) >= new Date();
  if (upcoming) {
    return <span className="crm-bcard__stage">{meetingLabel(chip.next_meeting_at, chip)}</span>;
  }
  const since = days(chip.last_meeting_at);
  if (since !== null && since >= 1) {
    return <span className="crm-bcard__stage">met {since}d ago — update?</span>;
  }
  return <span className="crm-bcard__stage crm-bcard__stage--quiet">no meeting on the calendar</span>;
}

/**
 * C: PRICING SENT — how long the numbers have been out. Dated from the stage
 * transition, so a card that reached the stage before we recorded transitions says
 * nothing rather than inventing an age.
 *
 * The audit state (line 2) is deliberately absent until the auditor DB has been
 * surveyed: "not audited" is a claim, and asserting it from missing data is the
 * mistake the agreement panel already made once.
 */
function PricingLine({ chip, company }) {
  const since = days(chip && chip.stage_entered_at);
  const value = company.monthly_value ? moneyK(company.monthly_value, company.currency) : null;
  if (since === null) {
    // A prospect at pricing-sent: the number this wants is their ad budget.
    return value ? null : <span className="crm-bcard__stage crm-bcard__stage--quiet">no budget recorded</span>;
  }
  return <span className="crm-bcard__stage">pricing sent {ago(since)}</span>;
}

/**
 * D: AGREEMENT — the document's own state. "Never viewed" is the limbo tell,
 * and it is only said when there ARE PandaDoc events and none is a view; with no
 * events at all the state is unknown and the line says nothing.
 */
function AgreementLine({ chip }) {
  if (!chip) return null;
  if (chip.doc_signed_at) {
    return <span className="crm-bcard__stage">signed {dateShort(chip.doc_signed_at)}</span>;
  }
  /*
   * "SENT" WAS NEVER A SEND DATE, which is why cards read "sent 33d ago · viewed
   * 34d ago" — viewed before sent, which no document does.
   *
   * There is no send date anywhere in this system to print. PandaDoc notifies on
   * 'has viewed' and 'Signed copy of' and NOT on send (src/watchers/pandadoc.js),
   * so the agreement stage is inferred from a VIEW and no send is ever observed.
   * `stage_entered_at` is what this line actually had: MAX of the transitions INTO
   * the current stage, while doc_viewed_at is MIN over every PandaDoc view ever
   * recorded for the company, unscoped by document and unwindowed
   * (src/actions/index.js). Opposite aggregations over different event sets, so
   * viewed-before-sent is structural rather than a bad row: a re-entry moves one
   * and not the other, and a previous deal's document still counts toward the
   * other.
   *
   * The two dates are NOT swapped and nothing is invented. The line now says what
   * it holds — how long the card has been in this column — and leaves "sent" for
   * whenever a real send date exists to print. Getting one needs the PandaDoc API
   * rather than its notification mail, which is the same access the signed
   * agreement link wants, so it should be scoped once.
   */
  const inStage = days(chip.stage_entered_at);
  const viewed = days(chip.doc_viewed_at);
  if (viewed !== null) {
    return (
      <span className="crm-bcard__stage">
        {inStage !== null ? `in agreement ${span(inStage)} · ` : ""}viewed {ago(viewed)}
      </span>
    );
  }
  // Still the limbo tell — there ARE document events and none of them is a view —
  // but it cannot claim a send date either, for the reason above.
  if (chip.doc_events > 0 && inStage !== null) {
    return <span className="crm-bcard__stage">in agreement {span(inStage)} · never viewed</span>;
  }
  // NO DOCUMENT EVENTS AT ALL — which is the common case, because PandaDoc's
  // mail is the only record of them and it doesn't always arrive. This branch
  // used to read "out for signature · 28d" off stage_entered_at, and both halves
  // were wrong: the age was the card's time in the column, not the document's,
  // and with no events behind it "out for signature" was a claim about a
  // document we have no state for. What IS known is when the prospect last
  // wrote, which is the number that decides whether to chase.
  const replied = days(chip.last_inbound_at);
  if (replied !== null) return <span className="crm-bcard__stage">replied {ago(replied)}</span>;
  return <span className="crm-bcard__stage crm-bcard__stage--quiet">no reply yet</span>;
}

export default function StageLine({ stage, company, chip }) {
  const line =
    stage === "new" ? <NewLine chip={chip} />
      : stage === "call_scheduled" ? <CallLine chip={chip} />
      : stage === "proposal_sent" ? <PricingLine chip={chip} company={company} />
      : stage === "agreement" ? <AgreementLine chip={chip} />
      : null;

  // Action items from a call have a tag of their own on the card (BoardPage), so
  // the one chip here is the most urgent of everything else.
  const top = chip && chip.actions && chip.actions.find((a) => a.kind !== CALL_ACTIONS);
  if (!line && !top) return null;

  return (
    <div className="crm-bcard__stageline">
      {line}
      {/* One chip, the most urgent. A card is 236px wide; a row of chips would push
          the company name out of it. */}
      {top && <ActionChip action={top} />}
    </div>
  );
}
